import React from 'react';
import PropTypes from 'prop-types';
import { Table, TBody, TR, TD } from 'oy-vey';
import Layout from '../layouts/Layout';
import Header from '../modules/Header';
import EmptySpace from '../modules/EmptySpace';
import EmailSignature from './EmailSignature';

// the host's scan report is ready.
//
// Sent when analysis completes, not when it fails: a failed analysis is an
// operations problem the host can do nothing about, and emailing it would train
// them to ignore these.
//
// The findings are summarised rather than embedded whole. A host wants
// "Front: Severe", not a JSON payload — and the report itself stays one click
// away rather than being copied into an inbox that will outlive any correction
// made to it.
class CarScanReportReady extends React.Component {
  static propTypes = {
    content: PropTypes.shape({
      hostName: PropTypes.string,
      reservationId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      listTitle: PropTypes.string,
      findings: PropTypes.array,
      damagedCount: PropTypes.number,
      inspectedCount: PropTypes.number,
      reportUrl: PropTypes.string,
      siteName: PropTypes.string,
      logo: PropTypes.string,
    }).isRequired
  };

  render() {
    const {
      content: {
        hostName, reservationId, listTitle, findings, damagedCount,
        inspectedCount, reportUrl, siteName, logo,
      }
    } = this.props;

    const textStyle = {
      color: '#333',
      backgroundColor: '#F7F7F7',
      fontFamily: 'Arial',
      fontSize: '16px',
      padding: '35px',
    };
    const linkStyle = { color: '#2d8f4b', fontWeight: 'bold', textDecoration: 'underline' };
    const findingStyle = { padding: '4px 0', fontSize: '15px' };

    // Every field is optional on purpose. The payload belongs to the analysis
    // service and will change; an email is a poor place to discover that.
    const parts = Array.isArray(findings) ? findings : [];
    const damaged = Number(damagedCount) || 0;

    // The headline is the answer to the only question a host actually has when
    // they open this. "No new damage" is the common case since the analysis service
    // prompt fixes, and it deserves to be said plainly rather than buried under
    // a list the host has to interpret.
    const headline = damaged > 0
      ? `The scan found new damage on ${damaged} ${damaged === 1 ? 'part' : 'parts'}.`
      : 'The scan found no new damage.';

    return (
      <Layout>
        <Header color="#2d8f4b" backgroundColor="#F7F7F7" logo={logo} siteName={siteName} />
        <div>
          <Table width="100%">
            <TBody>
              <TR>
                <TD style={textStyle}>
                  <EmptySpace height={20} />
                  <div>Hi {hostName || 'there'},</div>
                  <EmptySpace height={20} />
                  <div>
                    The damage scan for your trip{listTitle ? ` — ${listTitle}` : ''} has finished.
                  </div>
                  <EmptySpace height={20} />
                  <div><strong>{headline}</strong></div>
                  {inspectedCount > 0 && (
                    <div style={{ fontSize: '14px', color: '#666' }}>
                      {inspectedCount} {inspectedCount === 1 ? 'part' : 'parts'} inspected.
                    </div>
                  )}
                  <EmptySpace height={20} />
                  {parts.length > 0 && (
                    <div>
                      {parts.map((finding, index) => (
                        <div key={index} style={findingStyle}>
                          <strong>{finding.part}</strong>: {finding.severity}
                          {finding.analysis ? ` — ${finding.analysis}` : ''}
                        </div>
                      ))}
                    </div>
                  )}
                  <EmptySpace height={20} />
                  {reportUrl && (
                    <div>
                      <a href={reportUrl} style={linkStyle}>View the full report</a>
                    </div>
                  )}
                  <EmptySpace height={20} />
                  {/* The window is stated but not counted down here. It runs
                      from when the trip completed, and an email cannot know how
                      long ago that was by the time it is read. */}
                  <div style={{ fontSize: '14px', color: '#666' }}>
                    If you need to report damage, do so within the reporting window shown on the trip.
                    Reservation #{reservationId}.
                  </div>
                  <EmptySpace height={20} />
                  <EmailSignature siteName={siteName} />
                </TD>
              </TR>
            </TBody>
          </Table>
        </div>
      </Layout>
    );
  }
}

export default CarScanReportReady;
